import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore, apiClient } from '../store/auth.store';
import toast from 'react-hot-toast';

export default function Dashboard() {
  const { user } = useAuthStore();
  const [eventsCount, setEventsCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [recentEvents, setRecentEvents] = useState<any[]>([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data } = await apiClient.get('/events', {
        headers: { Authorization: `Bearer ${user?.apiToken}` },
      });
      setRecentEvents(data.events.slice(0, 5));
      setEventsCount(data.pagination.total);
      setTotalAmount(data.events.reduce((sum: number, e: any) => sum + e.amount, 0));
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const widgetUrl = `${window.location.origin}/widget/${user?.id}`;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      <p className="page-subtitle">Welcome back, {user?.username}!</p>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <div className="stat-value">{eventsCount}</div>
            <div className="stat-label">Total Events</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <div className="stat-value">${totalAmount.toFixed(2)}</div>
            <div className="stat-label">Total Amount</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔗</div>
          <div className="stat-info">
            <div className="stat-value">Active</div>
            <div className="stat-label">Widget Status</div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <h2>Quick Actions</h2>
          <div className="quick-actions">
            <Link to="/test" className="action-btn">
              <span className="action-icon">🧪</span>
              <div>
                <div className="action-title">Test Event</div>
                <div className="action-desc">Create a test donation</div>
              </div>
            </Link>
            <Link to="/widget-preview" className="action-btn">
              <span className="action-icon">👁️</span>
              <div>
                <div className="action-title">Widget Preview</div>
                <div className="action-desc">See your widget live</div>
              </div>
            </Link>
            <Link to="/settings" className="action-btn">
              <span className="action-icon">⚙️</span>
              <div>
                <div className="action-title">Settings</div>
                <div className="action-desc">Customize widget</div>
              </div>
            </Link>
            <Link to="/events" className="action-btn">
              <span className="action-icon">📋</span>
              <div>
                <div className="action-title">Event History</div>
                <div className="action-desc">View all donations</div>
              </div>
            </Link>
          </div>
        </div>

        <div className="card">
          <h2>Your Widget URL</h2>
          <div className="widget-url-box">
            <input type="text" value={widgetUrl} readOnly />
            <button onClick={() => copyToClipboard(widgetUrl)}>Copy</button>
          </div>
          <p className="helper-text">
            Use this URL in OBS/browser source to display your donation widget
          </p>

          <h3 style={{ marginTop: 20 }}>API Token</h3>
          <div className="widget-url-box">
            <input type="text" value={user?.apiToken || ''} readOnly />
            <button onClick={() => copyToClipboard(user?.apiToken || '')}>Copy</button>
          </div>
          <p className="helper-text">
            Use this token to create events via the API
          </p>
        </div>
      </div>

      {recentEvents.length > 0 && (
        <div className="card">
          <h2>Recent Events</h2>
          <div className="events-table">
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Amount</th>
                  <th>Message</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentEvents.map((event) => (
                  <tr key={event.id}>
                    <td>{event.username}</td>
                    <td style={{ color: 'var(--success)', fontWeight: 600 }}>
                      ${event.amount.toFixed(2)}
                    </td>
                    <td className="truncate">{event.message}</td>
                    <td>{new Date(event.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        .dashboard h1 {
          font-size: 28px;
          margin-bottom: 5px;
        }
        .page-subtitle {
          color: var(--text-secondary);
          font-size: 14px;
          margin-bottom: 30px;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }
        .stat-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 15px;
        }
        .stat-icon {
          font-size: 32px;
        }
        .stat-value {
          font-size: 24px;
          font-weight: 700;
        }
        .stat-label {
          font-size: 13px;
          color: var(--text-secondary);
        }
        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }
        .card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
        }
        .card h2 {
          font-size: 18px;
          margin-bottom: 20px;
        }
        .card h3 {
          font-size: 16px;
          margin-bottom: 12px;
          color: var(--text-secondary);
        }
        .quick-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .action-btn {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px;
          background: var(--bg-input);
          border-radius: 8px;
          text-decoration: none;
          color: var(--text-primary);
          transition: all 0.2s;
        }
        .action-btn:hover {
          background: var(--border);
          color: var(--text-primary);
        }
        .action-icon {
          font-size: 24px;
        }
        .action-title {
          font-weight: 600;
          font-size: 14px;
        }
        .action-desc {
          font-size: 12px;
          color: var(--text-secondary);
        }
        .widget-url-box {
          display: flex;
          gap: 10px;
        }
        .widget-url-box input {
          flex: 1;
          padding: 10px 14px;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: 6px;
          color: var(--text-secondary);
          font-size: 13px;
          font-family: monospace;
        }
        .widget-url-box button {
          padding: 10px 20px;
          background: var(--primary);
          border: none;
          border-radius: 6px;
          color: white;
          font-weight: 600;
          font-size: 13px;
          white-space: nowrap;
        }
        .helper-text {
          font-size: 12px;
          color: var(--text-secondary);
          margin-top: 8px;
        }
        .events-table {
          overflow-x: auto;
        }
        .events-table table {
          width: 100%;
          border-collapse: collapse;
        }
        .events-table th {
          text-align: left;
          padding: 12px;
          font-size: 13px;
          color: var(--text-secondary);
          border-bottom: 1px solid var(--border);
        }
        .events-table td {
          padding: 12px;
          font-size: 14px;
          border-bottom: 1px solid var(--border);
        }
        .truncate {
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        @media (max-width: 768px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
